import {
  type ArchiveEstimate,
  type BulkArchivePrepareResult,
  type BulkArchiveRequest,
  type BulkDeleteFilesResult,
  type BulkDeleteFoldersResult,
  type BulkDeleteImpact,
  type BulkMoveFilesResult,
  type BulkMoveFoldersResult,
  type DeletionBatch,
  type DeletionBatchItems,
  type DeletionBatchList,
  type ExpiredFileList,
  type ExtendTtlResult,
  type FolderDeleteImpact,
  type FolderListing,
  type FolderNode,
  type ListingOnly,
  type PurgeBeforePreview,
  type PurgeCountsResult,
  type PurgeFilesResult,
  type RestoreBatchResult,
  type RestoreExpiredResult,
  type SpacesResponse,
  type ThumbnailSize,
  type TtlKind,
  archiveEstimateSchema,
  bulkArchivePrepareResultSchema,
  bulkDeleteFilesResultSchema,
  bulkDeleteFoldersResultSchema,
  bulkDeleteImpactSchema,
  bulkMoveFilesResultSchema,
  bulkMoveFoldersResultSchema,
  deletionBatchItemsSchema,
  deletionBatchListSchema,
  deletionBatchSchema,
  expiredFileListSchema,
  extendTtlResultSchema,
  folderDeleteImpactSchema,
  folderListingSchema,
  folderNodeSchema,
  purgeBeforePreviewSchema,
  purgeCountsResultSchema,
  purgeFilesResultSchema,
  restoreBatchResultSchema,
  restoreExpiredResultSchema,
  spacesResponseSchema,
} from '@xfile/shared';
import { apiFetch } from '../lib/http';
import { withSlotParam } from '../lib/activeSlot';

/**
 * Client typé de l'API de stockage (XFILE-01, -02). Consomme les contrats partagés
 * `@xfile/shared` (jamais redéfinis ici) et passe par `apiFetch` (cookie de session +
 * `X-Active-Slot` + traçage diagnostic). Les erreurs de l'API (`{ error: { code } }`)
 * sont normalisées en `StorageApiError` (code + statut) que l'UI mappe en message.
 */

export class StorageApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'StorageApiError';
  }
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON.
  }
  return 'UNKNOWN';
}

/** Chemin du listing : racine (`null`) ou dossier ciblé. */
function listingPath(folderId: string | null): string {
  return folderId === null ? '/api/folders' : `/api/folders/${folderId}`;
}

/**
 * ESPACES visibles par l'appelant (`GET /api/spaces`, XFILE-25) : toujours public/auto/api + la
 * racine PERSO de l'appelant ; un admin voit EN PLUS les racines `user` des autres comptes. Chaque
 * entrée porte l'id de sa RACINE (à ouvrir pour naviguer dedans). Amorce la page d'accueil
 * (`SpacesHome`). Lève sur toute réponse non-OK.
 */
export async function fetchSpaces(signal?: AbortSignal): Promise<SpacesResponse> {
  const res = await apiFetch('/api/spaces', { signal: signal ?? null });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return spacesResponseSchema.parse(await res.json());
}

/**
 * Cible de route d'un dossier dans l'explorateur AUTHENTIFIÉ (racine → `/`, sinon
 * `/folder/:id`). Source unique de navigation. `/d/:…` est RÉSERVÉ au partage PUBLIC
 * (ADR-0005) — d'où le préfixe `/folder/` ici pour éviter toute collision.
 */
export function folderHref(id: string | null): string {
  return id === null ? '/' : `/folder/${id}`;
}

/**
 * Listing d'un dossier (racine si `folderId` null). Lève sur toute réponse non-OK.
 *
 * `only: 'folders'` (XFILE-57) demande au serveur de NE PAS assembler les fichiers ni les agrégats
 * TTL récursifs — pour un appelant qui ne lit que `folders`/`breadcrumb` (cf. `FolderPicker`). La
 * réponse garde la MÊME forme (`files: []`, `ttl` absent), donc le schéma de parse est inchangé.
 * OMIS ⇒ listing complet, rigoureusement comme avant.
 */
export async function fetchListing(
  folderId: string | null,
  signal?: AbortSignal,
  options?: { only?: ListingOnly },
): Promise<FolderListing> {
  const path = listingPath(folderId);
  const url = options?.only ? `${path}?only=${encodeURIComponent(options.only)}` : path;
  const res = await apiFetch(url, { signal: signal ?? null });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return folderListingSchema.parse(await res.json());
}

/** Crée un dossier `user` sous `parentId` (`null` = racine). Lève sur 4xx (code métier). */
export async function createFolder(parentId: string | null, name: string): Promise<FolderNode> {
  const res = await apiFetch('/api/folders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ parentId, name }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return folderNodeSchema.parse(await res.json());
}

/** Renomme un dossier `user` (`PATCH { name }`). Lève sur 4xx (CONFLICT / FORBIDDEN / NOT_FOUND). */
export async function renameFolder(id: string, name: string): Promise<FolderNode> {
  const res = await apiFetch(`/api/folders/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return folderNodeSchema.parse(await res.json());
}

/**
 * Déplace un dossier `user` sous un nouveau parent (`PATCH { parentId }`). `parentId = null`
 * = déplacement vers la RACINE. Lève sur 4xx : 400 VALIDATION (cycle : dans soi-même/un
 * descendant), 403 FORBIDDEN (dossier `auto`), 404 NOT_FOUND (dossier ou parent), 409 CONFLICT
 * (nom déjà pris à destination).
 */
export async function moveFolder(id: string, parentId: string | null): Promise<FolderNode> {
  const res = await apiFetch(`/api/folders/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ parentId }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return folderNodeSchema.parse(await res.json());
}

/**
 * Supprime (soft-delete) un dossier `user` — RÉCURSIF côté serveur : le dossier ET tout son
 * sous-arbre actif (sous-dossiers + fichiers) partent avec. Lève sur 4xx : 403 FORBIDDEN
 * (dossier `auto`), 404 NOT_FOUND.
 *
 * Renvoie l'ESTAMPILLE du lot (XFILE-33) : l'uuid posé sur les seules lignes que CET appel a fait
 * basculer, à rejouer dans {@link restoreDeletion} pour ANNULER exactement cette suppression.
 */
export async function deleteFolder(id: string): Promise<DeletionBatch> {
  const res = await apiFetch(`/api/folders/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return deletionBatchSchema.parse(await res.json());
}

/**
 * IMPACT d'un effacement de dossier (`GET /api/folders/:id/delete-impact`, XFILE-33) : ce que le
 * `DELETE` DÉTRUIRAIT, agrégé récursivement sur le sous-arbre — sous-dossiers, profondeur, fichiers
 * et durée de vie. Sert à CHIFFRER la modale de confirmation AVANT d'agir. Mêmes gardes que le
 * `DELETE` côté serveur. Lève sur 4xx (403 FORBIDDEN, 404 NOT_FOUND) — l'appelant DÉGRADE alors
 * silencieusement vers le message générique (une confirmation ne montre jamais d'écran d'erreur).
 */
export async function fetchFolderDeleteImpact(id: string): Promise<FolderDeleteImpact> {
  const res = await apiFetch(`/api/folders/${id}/delete-impact`);
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return folderDeleteImpactSchema.parse(await res.json());
}

/**
 * CORBEILLE (`GET /api/deletions?before=&limit=`, XFILE-44) — les lots de suppression encore
 * RESTAURABLES, du plus récent au plus ancien, paginés par CURSEUR (`nextBefore` ; absent = fin de
 * liste).
 *
 * ⚠ `before` est OPAQUE : on repasse `nextBefore` TEL QUEL, sans jamais le lire comme une date. Sa
 * forme (`<ISO>|<uuid>`) appartient au serveur, qui la doit aux lots partageant la milliseconde.
 * ⚠ Une page VIDE accompagnée d'un `nextBefore` n'est PAS la fin de la liste : le filtre de droits
 * s'applique après la pagination, et le balayage a pu ne croiser que des lots d'autrui.
 *
 * Comble la plus forte asymétrie du produit : `deleted_batch` rend chaque suppression restaurable
 * pendant 30 JOURS, mais la seule affordance d'annulation vivait 8 SECONDES dans un toaster.
 *
 * ⚠ Route AUTHENTIFIÉE ORDINAIRE, PAS une route d'administration — d'où sa place ici et non dans
 * `admin/admin-api.ts` où elle avait atterri au lot 7. Le serveur ne rend que les lots dont
 * l'appelant peut GÉRER le point de rattachement (`folderCapabilities(...).canManage`) : la
 * corbeille n'est pas une fenêtre sur les suppressions des autres, et un `user` n'y voit donc que
 * son espace personnel et l'espace Public. `restorable: false` ⇒ la restauration répondrait 409
 * (emplacement d'origine disparu ou homonyme actif recréé).
 */
export async function fetchDeletions(
  opts: { before?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<DeletionBatchList> {
  const params = new URLSearchParams();
  if (opts.before != null) params.set('before', opts.before);
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const query = params.toString();
  const res = await apiFetch(`/api/deletions${query ? `?${query}` : ''}`, {
    signal: signal ?? null,
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return deletionBatchListSchema.parse(await res.json());
}

/**
 * ANNULE une suppression estampillée (`POST /api/deletions/:batch/restore`, XFILE-33) : ressuscite
 * EXACTEMENT le lot — jamais ce qui avait été effacé avant et indépendamment. NON idempotent par
 * construction (rejouer un lot déjà restauré → 404). Lève sur 4xx :
 *  - 409 `RESTORE_CONFLICT` : un homonyme actif a été recréé entre-temps ;
 *  - 409 `CONFLICT` : l'emplacement d'origine (dossier parent) n'existe plus ;
 *  - 404 NOT_FOUND : lot inconnu, déjà restauré, ou purgé définitivement ; 403 FORBIDDEN.
 */
export async function restoreDeletion(batch: string): Promise<RestoreBatchResult> {
  const res = await apiFetch(`/api/deletions/${batch}/restore`, { method: 'POST' });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return restoreBatchResultSchema.parse(await res.json());
}

/**
 * RESTAURE un lot de suppression MANUELLE vers un dossier de destination CHOISI
 * (`POST /api/deletions/:batch/restore-to`, XFILE-75), et NON à son emplacement d'origine. Les
 * RACINES du lot y atterrissent (dédup de nom sur collision), chaque fichier repart avec un TTL
 * PLANCHER de 24 h, et les liens de partage des éléments restaurés sont RÉVOQUÉS (remettre en ligne
 * exige de re-partager).
 *
 * ⚠ DISTINCTE de {@link restoreDeletion} (`.../restore`, sans corps) — l'undo IMMÉDIAT du toaster,
 * à l'ORIGINE, ouvert à tout authentifié, qui NE bouge pas. Celle-ci est réservée aux admins côté
 * serveur et choisit toujours OÙ restaurer, ce qui rend le champ `restorable` (collision à l'origine)
 * sans objet. Lève sur 4xx : 403 (non-admin, ou destination non déposable), 404 (destination ou lot
 * inconnus). `purgedFiles > 0` ⇒ succès PARTIEL à DIRE (contenu déjà purgé), comme {@link restoreDeletion}.
 */
export async function restoreBatchToDestination(
  batch: string,
  destFolderId: string,
): Promise<RestoreBatchResult> {
  const res = await apiFetch(`/api/deletions/${batch}/restore-to`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ destFolderId }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return restoreBatchResultSchema.parse(await res.json());
}

/**
 * CONTENU d'un lot de suppression (`GET /api/deletions/:batch/items`, XFILE-75) — les fichiers et
 * dossiers du lot avec leur chemin RELATIF, pour DÉPLIER un lot et y consulter/télécharger les
 * fichiers (`hasContent`) via {@link trashedFileContentUrl} / {@link trashedFileThumbnailUrl}, SANS
 * le restaurer. Ne restaure ni ne purge rien. Lève sur 4xx (404 lot inconnu).
 */
export async function fetchBatchItems(
  batch: string,
  signal?: AbortSignal,
): Promise<DeletionBatchItems> {
  const res = await apiFetch(`/api/deletions/${batch}/items`, { signal: signal ?? null });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return deletionBatchItemsSchema.parse(await res.json());
}

/**
 * Soft-delete un LOT de fichiers par ids (`POST /api/files/bulk-delete`). L'appelant borne le
 * lot à `BULK_FILES_MAX`. Lève sur 4xx (400 VALIDATION, 403 FORBIDDEN). Renvoie le nombre
 * réellement supprimé et, si quelque chose a basculé, l'ESTAMPILLE du lot (XFILE-33) —
 * `batch` est ABSENT quand `deleted === 0` (rejeu idempotent : rien à annuler).
 */
export async function bulkDeleteFiles(ids: string[]): Promise<BulkDeleteFilesResult> {
  const res = await apiFetch('/api/files/bulk-delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return bulkDeleteFilesResultSchema.parse(await res.json());
}

/**
 * Déplace un LOT de fichiers vers `folderId` (`POST /api/files/bulk-move`). Lève sur 4xx :
 * 400 VALIDATION, 403 FORBIDDEN, 404 NOT_FOUND (destination introuvable), 409 CONFLICT (collision
 * de nom à destination). Renvoie le nombre réellement déplacé.
 */
export async function bulkMoveFiles(ids: string[], folderId: string): Promise<BulkMoveFilesResult> {
  const res = await apiFetch('/api/files/bulk-move', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, folderId }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return bulkMoveFilesResultSchema.parse(await res.json());
}

/**
 * Soft-delete un LOT de DOSSIERS par ids (`POST /api/folders/bulk-delete`, XFILE-45) — chacun
 * emportant tout son sous-arbre, comme la suppression unitaire.
 *
 * REMPLACE une boucle séquentielle d'appels unitaires, et le gain n'est PAS d'abord la latence :
 * la boucle produisait UNE ESTAMPILLE PAR DOSSIER, donc « Annuler » n'était plus une action mais
 * N — et un échec au i-ème tour laissait la corbeille à moitié restaurée, sans que le `catch`
 * sache seulement le dire. Ici, UN SEUL `batch` couvre tout le geste : une requête pour
 * supprimer, une pour annuler. `batch` est ABSENT quand rien n'a basculé (rien à annuler).
 *
 * L'appelant borne le lot à `BULK_FOLDERS_MAX`. Lève sur 4xx (403 FORBIDDEN, 404 NOT_FOUND) —
 * la portée est vérifiée AVANT toute écriture, donc un id hors périmètre rejette le lot ENTIER
 * sans effacement partiel.
 */
export async function bulkDeleteFolders(ids: string[]): Promise<BulkDeleteFoldersResult> {
  const res = await apiFetch('/api/folders/bulk-delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return bulkDeleteFoldersResultSchema.parse(await res.json());
}

/**
 * Déplace un LOT de DOSSIERS sous `parentId` (`POST /api/folders/bulk-move`, XFILE-45).
 * `parentId = null` = racine. TOUT OU RIEN côté serveur : un déplacement à moitié appliqué
 * éparpillerait la sélection entre deux emplacements sans trace de l'état d'origine.
 *
 * Mêmes gardes que le déplacement unitaire, appliquées à chaque id : 400 VALIDATION (cycle :
 * dans soi-même ou un descendant), 403 FORBIDDEN (`auto` immuable), 404 NOT_FOUND, 409 CONFLICT
 * (collision de nom à destination).
 */
export async function bulkMoveFolders(
  ids: string[],
  parentId: string | null,
): Promise<BulkMoveFoldersResult> {
  const res = await apiFetch('/api/folders/bulk-move', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, parentId }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return bulkMoveFoldersResultSchema.parse(await res.json());
}

/**
 * IMPACT d'un effacement de SÉLECTION (`POST /api/folders/bulk-delete-impact`, XFILE-45) :
 * dossiers ET fichiers isolés cochés, agrégés en UNE passe serveur.
 *
 * REMPLACE N appels à {@link fetchFolderDeleteImpact} lancés EN PARALLÈLE (jusqu'à 25 CTE
 * récursives concurrentes depuis un navigateur, borne posée en v2.2.0 pour contenir la casse),
 * et corrige au passage un défaut que la somme d'appels unitaires ne pouvait pas voir : deux
 * dossiers cochés dont l'un DESCEND de l'autre ADDITIONNAIENT leurs contenus — le même fichier
 * compté deux fois. L'union est ici dédupliquée côté serveur.
 *
 * Même DÉGRADATION SILENCIEUSE que l'impact unitaire : l'appelant retombe sur le message
 * générique si l'appel échoue — une confirmation ne montre jamais d'écran d'erreur.
 */
export async function fetchBulkDeleteImpact(
  folderIds: string[],
  fileIds: string[],
): Promise<BulkDeleteImpact> {
  const res = await apiFetch('/api/folders/bulk-delete-impact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folderIds, fileIds }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return bulkDeleteImpactSchema.parse(await res.json());
}

/**
 * PROLONGE (PLANCHER-SEULEMENT) la conservation de TOUT le sous-arbre de fichiers actifs d'un
 * dossier (`POST /api/folders/:id/extend-ttl`). OUVERT À TOUT UTILISATEUR AUTHENTIFIÉ (opération
 * non destructive : ne peut qu'allonger la vie). Lève sur 4xx (404 NOT_FOUND). Renvoie le nombre
 * de fichiers réellement prolongés.
 */
export async function extendFolderTtl(id: string, ttlKind: TtlKind): Promise<ExtendTtlResult> {
  const res = await apiFetch(`/api/folders/${id}/extend-ttl`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ttlKind }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return extendTtlResultSchema.parse(await res.json());
}

/**
 * PROLONGE (PLANCHER-SEULEMENT) la conservation d'un fichier actif (`POST
 * /api/files/:id/extend-ttl`). OUVERT À TOUT UTILISATEUR AUTHENTIFIÉ. Lève sur 4xx (404
 * NOT_FOUND). Renvoie `{ extended: 0 | 1 }`.
 */
export async function extendFileTtl(id: string, ttlKind: TtlKind): Promise<ExtendTtlResult> {
  const res = await apiFetch(`/api/files/${id}/extend-ttl`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ttlKind }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return extendTtlResultSchema.parse(await res.json());
}

/**
 * ESTIME le contenu d'un téléchargement ZIP d'un dossier (`GET /api/folders/:id/archive/estimate`),
 * agrégé RÉCURSIVEMENT sur tout son sous-arbre : nombre de fichiers avec contenu et volume total
 * (octets non compressés). OUVERT À TOUT UTILISATEUR AUTHENTIFIÉ (lecture). Sert à ANNONCER le
 * volume AVANT de lancer le téléchargement. Lève sur réponse non-OK (404 NOT_FOUND).
 */
export async function fetchArchiveEstimate(id: string): Promise<ArchiveEstimate> {
  const res = await apiFetch(`/api/folders/${id}/archive/estimate`);
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return archiveEstimateSchema.parse(await res.json());
}

/**
 * URL de TÉLÉCHARGEMENT ZIP d'un dossier (`GET /api/folders/:id/archive.zip`), avec le slot actif
 * en query (`?slot=N`). Rendue en `<a href download>` cliqué par programme (téléchargement
 * browser-natif) : le slot NE peut pas passer par en-tête, comme le téléchargement d'un fichier.
 * Le serveur répond en `Content-Disposition: attachment` (le navigateur enregistre l'archive).
 */
export function folderArchiveUrl(id: string): string {
  return withSlotParam(`/api/folders/${id}/archive.zip`);
}

/**
 * ESTIME le contenu d'un téléchargement ZIP d'une SÉLECTION (XFILE-27) — fichiers isolés
 * (`fileIds`, à la racine du ZIP) et/ou dossiers (`folderIds`, inclus comme sous-arbres
 * récursifs) — via `POST /api/archive/estimate`. Renvoie `{ count, totalBytes }` (agrégat des
 * fichiers actifs avec contenu). Sert à ANNONCER le volume AVANT de lancer le téléchargement.
 * Lève sur réponse non-OK (403 si un élément n'est pas lisible, 404 si un id est absent).
 */
export async function archiveSelectionEstimate(sel: BulkArchiveRequest): Promise<ArchiveEstimate> {
  const res = await apiFetch('/api/archive/estimate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sel),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return archiveEstimateSchema.parse(await res.json());
}

/**
 * PRÉPARE le téléchargement ZIP d'une SÉLECTION (XFILE-27) via `POST /api/archive/prepare` :
 * valide les droits de lecture puis renvoie un JETON opaque à courte durée de vie (≈ 2 min)
 * liant l'appelant à sa sélection. Le client passe ensuite ce jeton à
 * {@link archiveSelectionUrl}. Lève sur réponse non-OK (403 non lisible, 404 absent, 400 vide).
 */
export async function archiveSelectionPrepare(
  sel: BulkArchiveRequest,
): Promise<BulkArchivePrepareResult> {
  const res = await apiFetch('/api/archive/prepare', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sel),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return bulkArchivePrepareResultSchema.parse(await res.json());
}

/**
 * URL de TÉLÉCHARGEMENT ZIP d'une sélection préparée (`GET /api/archive.zip?token=…`), avec le
 * slot actif ajouté en query (`&slot=N`) comme {@link folderArchiveUrl}. Rendue en `<a href
 * download>` cliqué par programme (téléchargement browser-natif : le slot ne peut pas passer par
 * en-tête). Le serveur répond en `Content-Disposition: attachment`.
 */
export function archiveSelectionUrl(token: string): string {
  return withSlotParam(`/api/archive.zip?token=${encodeURIComponent(token)}`);
}

/**
 * CORBEILLE DES EXPIRÉS (`GET /api/deletions/expired?before=&limit=`, XFILE-74) — les fichiers
 * INDIVIDUELS échus par TTL dont le contenu est encore retenu (≤ `EXPIRED_TRASH_RETENTION_DAYS`
 * jours), du plus RÉCEMMENT expiré au plus ancien, paginés par CURSEUR OPAQUE (`nextBefore` ; absent
 * = fin de liste).
 *
 * ⚠ `before` est OPAQUE (`<ISO µs>|<uuid>`) : on repasse `nextBefore` TEL QUEL, sans jamais le lire
 * comme une date — sa forme appartient au serveur, qui la doit aux fichiers partageant la milliseconde.
 * ⚠ RÉSERVÉE À L'ADMINISTRATION : la garde vit sur la ROUTE (`AdminRoute`) et côté serveur (403 pour
 * un `user`) ; ce client ne connaît aucun rôle.
 *
 * DISJOINTE de la corbeille des SUPPRESSIONS ({@link fetchDeletions}) : une expiration pose
 * `expired_at` et JAMAIS `deleted_batch`, une suppression manuelle l'inverse — les deux endpoints ne
 * se recouvrent pas. Les expirés sont restaurés AU CAS PAR CAS vers un dossier CHOISI, jamais remis
 * en bloc à leur emplacement d'origine.
 */
export async function fetchExpiredFiles(
  opts: { before?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<ExpiredFileList> {
  const params = new URLSearchParams();
  if (opts.before != null) params.set('before', opts.before);
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const query = params.toString();
  const res = await apiFetch(`/api/deletions/expired${query ? `?${query}` : ''}`, {
    signal: signal ?? null,
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return expiredFileListSchema.parse(await res.json());
}

/**
 * RESTAURE une SÉLECTION de fichiers expirés vers UN dossier de destination COMMUN
 * (`POST /api/deletions/expired/restore`, XFILE-74). Les fichiers repartent avec un TTL de 1 jour
 * (`EXPIRED_RESTORE_TTL_KIND`) et leur ANCIEN lien de partage est révoqué — il faut les repartager.
 * L'appelant borne le lot à `BULK_FILES_MAX`.
 *
 * `restored` = fichiers effectivement remis en ligne ; `skipped` = ids IGNORÉS (contenu déjà purgé,
 * inconnu, ou plus en corbeille des expirés) — à DIRE à l'utilisateur, comme le `purgedFiles` de la
 * corbeille des suppressions. Lève sur 4xx : 403 (dépôt non autorisé dans la destination),
 * 404 (destination introuvable), 400 (validation).
 */
export async function restoreExpiredFiles(
  fileIds: string[],
  destFolderId: string,
): Promise<RestoreExpiredResult> {
  const res = await apiFetch('/api/deletions/expired/restore', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileIds, destFolderId }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return restoreExpiredResultSchema.parse(await res.json());
}

/**
 * PURGE DÉFINITIVE d'un lot de suppression (`POST /api/deletions/:batch/purge`, XFILE-76) —
 * IRRÉVERSIBLE. Ne touche QUE des lignes soft-deleted (content-first, dédup-aware côté serveur).
 * Renvoie les comptes RÉELLEMENT effacés (`folders` peut être < au lot si un dossier reste référencé
 * par un fichier subsistant). Réservée aux admins côté serveur. Lève sur 4xx (404 lot inconnu, 403).
 */
export async function purgeBatch(batch: string): Promise<PurgeCountsResult> {
  const res = await apiFetch(`/api/deletions/${batch}/purge`, { method: 'POST' });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return purgeCountsResultSchema.parse(await res.json());
}

/**
 * PURGE DÉFINITIVE d'une sélection de fichiers EN CORBEILLE (expirés OU supprimés manuellement), par
 * ids (`POST /api/deletions/files/purge`, XFILE-76) — IRRÉVERSIBLE. Un id ACTIF ou inconnu est IGNORÉ
 * (`skipped`, à DIRE à l'utilisateur). L'appelant borne le lot à `BULK_FILES_MAX`. Lève sur 4xx
 * (400 VALIDATION, 403 FORBIDDEN).
 */
export async function purgeFiles(fileIds: string[]): Promise<PurgeFilesResult> {
  const res = await apiFetch('/api/deletions/files/purge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileIds }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return purgeFilesResultSchema.parse(await res.json());
}

/**
 * VIDE la corbeille avant une date (`POST /api/deletions/purge-before`, XFILE-76) : purge
 * DÉFINITIVEMENT tout le soft-deleted (LES DEUX corbeilles — expirés ET suppressions) dont
 * `deleted_at < before`. `before` est un datetime ISO 8601 AVEC offset (le web mappe le sélecteur de
 * date vers une FIN de journée locale, cf. `TrashPage`). IRRÉVERSIBLE. Lève sur 4xx (400 date
 * invalide, 403).
 */
export async function purgeBefore(before: string): Promise<PurgeCountsResult> {
  const res = await apiFetch('/api/deletions/purge-before', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ before }),
  });
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return purgeCountsResultSchema.parse(await res.json());
}

/**
 * APERÇU chiffré du vidage avant une date (`GET /api/deletions/purge-before/preview?before=`,
 * XFILE-76) : `{ files, folders, bytes }` que la purge effacerait — pour une confirmation HONNÊTE
 * avant d'agir. Ne supprime rien. Lève sur 4xx (400 date invalide, 403).
 */
export async function fetchPurgeBeforePreview(
  before: string,
  signal?: AbortSignal,
): Promise<PurgeBeforePreview> {
  const res = await apiFetch(
    `/api/deletions/purge-before/preview?before=${encodeURIComponent(before)}`,
    { signal: signal ?? null },
  );
  if (!res.ok) throw new StorageApiError(res.status, await errorCodeOf(res));
  return purgeBeforePreviewSchema.parse(await res.json());
}

/**
 * URL du CONTENU d'un fichier EN CORBEILLE (`GET /api/deletions/file/:id/content`, XFILE-75/-76),
 * avec le slot actif en query — rendue en `<a href>` / `<video src>` / `<img>` browser-natif (le slot
 * ne peut pas passer par en-tête). Sert INLINE par défaut (visionneuse) ; `download: true` ajoute
 * `?download=1` pour forcer le téléchargement.
 *
 * ⚠ ROUTE DÉDIÉE à la corbeille : un fichier soft-deleted, que `GET /api/files/:id/content` REJETTE.
 */
export function trashedFileContentUrl(id: string, opts: { download?: boolean } = {}): string {
  const base = `/api/deletions/file/${id}/content`;
  return withSlotParam(opts.download ? `${base}?download=1` : base);
}

/**
 * URL de la VIGNETTE d'un fichier EN CORBEILLE (`GET /api/deletions/file/:id/thumbnail/:size`,
 * XFILE-75) — expirés ET supprimés manuellement —, avec le slot actif en query (`?slot=N`), rendue en
 * `<img>` browser-natif comme {@link fileThumbnailUrl}. Le serveur sert un WebP si une vignette
 * existe, sinon une icône de type SVG — jamais un 404 cassant.
 *
 * ⚠ ROUTE DÉDIÉE, à ne pas confondre avec {@link fileThumbnailUrl} : un fichier de la corbeille est
 * SOFT-DELETED, et la route des fichiers actifs le REJETTE.
 */
export function trashedFileThumbnailUrl(id: string, size: ThumbnailSize): string {
  return withSlotParam(`/api/deletions/file/${id}/thumbnail/${size}`);
}
